import { computed } from 'vue'
import { useMatchStore } from '@/stores/match.js'
import { useAuthStore } from '@/stores/auth.js'
import checkoutService from '@/services/checkout.js'
import { useToast } from '@/composables/useToast.js'
import { useTranslationsStore } from '@/stores/translations.js'

export function useDartScoring(currentPlayer) {
    const matchStore = useMatchStore()
    const authStore = useAuthStore()
    const { addToast } = useToast()
    const { t } = useTranslationsStore()

    const settings = computed(() => matchStore.getSettings)
    const checkInType = computed(() => settings.value.check_in)
    const checkOutType = computed(() => settings.value.check_out)

    const requiresDoubleIn = computed(() => {
        const checkIn = checkInType.value
        return checkIn === 'double' || checkIn === 'master'
    })

    const requiresDoubleOrTripleIn = computed(() => {
        const checkIn = checkInType.value
        return checkIn === 'master'
    })

    const requiresDoubleOut = computed(() => {
        return checkOutType.value === 'double'
    })

    const requiresMasterOut = computed(() => {
        return checkOutType.value === 'master'
    })

    const requiresSpecialOut = computed(() => {
        return requiresDoubleOut.value || requiresMasterOut.value
    })

    const isFirstTurnInLeg = computed(() => {
        // currentPlayer is briefly null while the match tears down (e.g. the
        // winning checkout arrives over the websocket), so guard against it.
        const player = currentPlayer.value
        if (!player) return true

        const currentLeg = matchStore.getCurrentLeg
        if (!currentLeg?.turns?.length) return true

        return !currentLeg.turns.some(
            (turn) => turn.team === player.team && turn.thrown_score > 0,
        )
    })

    const maxScore = computed(() => {
        return isFirstTurnInLeg.value && requiresDoubleIn.value ? 160 : 180
    })

    const remainingScore = computed(() => {
        const player = currentPlayer.value
        if (!player) return null
        try {
            return matchStore.getCompetitorRemainingScore(player.team)
        } catch {
            return null
        }
    })

    const canCheckout = computed(() => {
        if (!remainingScore.value || remainingScore.value <= 0) return false
        if (!requiresSpecialOut.value) return true

        if (requiresMasterOut.value) {
            return checkoutService.hasMasterCheckout(remainingScore.value)
        }

        return checkoutService.hasCheckout(remainingScore.value)
    })

    function isValidCheckoutScore(score) {
        const newRemaining = remainingScore.value - score

        if (newRemaining < 0) return false

        if (requiresDoubleOut.value) {
            if (newRemaining === 0) {
                return checkoutService.hasCheckout(remainingScore.value)
            }
            if (newRemaining === 1) return false
        }

        if (requiresMasterOut.value) {
            if (newRemaining === 0) {
                return checkoutService.hasMasterCheckout(remainingScore.value)
            }
            if (newRemaining === 1) return false
        }

        return true
    }

    function isDartValidForCheckout(dartType) {
        if (!requiresSpecialOut.value) return true

        if (requiresDoubleOut.value) {
            return dartType === 'D' || dartType === 'B.E'
        }

        if (requiresMasterOut.value) {
            return dartType === 'D' || dartType === 'T' || dartType === 'B.E'
        }

        return true
    }

    const effectiveCheckOutType = computed(() =>
        requiresMasterOut.value ? 'master' : requiresDoubleOut.value ? 'double' : 'single',
    )

    // The remaining score at the start of the turn. While editing a turn the
    // live remaining already excludes that turn, so its score is added back.
    const effectiveRemainingBefore = computed(() => {
        let remainingBefore = remainingScore.value

        if (matchStore.editingTurn) {
            const editingCompetitorId = matchStore.editingTurn.competitorId
            const editingRemainingNow =
                matchStore.getCompetitorRemainingScore(editingCompetitorId)
            remainingBefore = editingRemainingNow + matchStore.editingTurn.thrown_score
        }

        return remainingBefore
    })

    const completedHumanTurns = () => {
        const currentLeg = matchStore.getCurrentLeg
        if (!currentLeg?.turns?.length) return []

        const turns = currentLeg.turns.filter(
            (turn) =>
                turn.thrown_score !== undefined &&
                turn.thrown_score !== -1 &&
                !matchStore.isTurnFromBot(turn),
        )

        // In remote play everyone has their own device, so "redo" may only ever
        // touch your own last turn, never one thrown by another remote player.
        // In local (single-device) play there's only one shared thrower, so the
        // actual most recent turn from anyone is fair game.
        if (matchStore.isRemotePlay) {
            const ownProfileId = authStore.profile?.id
            return turns.filter((turn) => String(turn.profile_id) === String(ownProfileId))
        }

        return turns
    }

    const hasLastTurn = computed(() => completedHumanTurns().length > 0)

    // Puts the player's most recent completed turn into edit mode. `redoHintKey`
    // is the i18n key for the grid-specific hint shown the first time (e.g.
    // "Click CLEAR…" vs "Click UNDO…").
    function loadLastTurn(redoHintKey) {
        const currentLeg = matchStore.getCurrentLeg
        const completedTurns = completedHumanTurns()
        if (completedTurns.length === 0) return

        const hasSeenRedoTooltip =
            localStorage.getItem('dartshopper-redo-tooltip-seen') === 'true'
        if (!hasSeenRedoTooltip) {
            addToast(t('score_grid.editing_turn_title'), 5000, t(redoHintKey))
            localStorage.setItem('dartshopper-redo-tooltip-seen', 'true')
        }

        const lastTurn = completedTurns[completedTurns.length - 1]
        const competitorId = lastTurn.team
        const competitorTurns = currentLeg.turns.filter((turn) => turn.team === competitorId)
        const turnIndex = competitorTurns.findIndex((t) => t === lastTurn)

        matchStore.setEditingTurn(competitorId, turnIndex, {
            score: lastTurn.thrown_score,
            darts: lastTurn.darts || [],
        })
    }

    async function submitDartData(payload) {
        if (!matchStore.match.id) {
            console.error('No active match to submit dart to')
            return
        }

        return await matchStore.submitDart({
            profile_id: currentPlayer.value.id,
            ...payload,
        })
    }

    return {
        matchStore,
        settings,
        checkInType,
        checkOutType,
        requiresDoubleIn,
        requiresDoubleOrTripleIn,
        requiresDoubleOut,
        requiresMasterOut,
        requiresSpecialOut,
        isFirstTurnInLeg,
        maxScore,
        remainingScore,
        canCheckout,
        effectiveCheckOutType,
        effectiveRemainingBefore,
        hasLastTurn,
        loadLastTurn,
        isValidCheckoutScore,
        isDartValidForCheckout,
        submitDartData,
    }
}
